import React, { FC, useState } from 'react';
import moment from 'moment';
import DOMPurify from 'dompurify';
import {
    Button,
    Checkbox,
    Column,
    Container,
    EmptyListWarning,
    Grid,
    ModalWindow,
    Row,
    TextInput,
    Title,
} from 'components';
import { useUrlQueryParamsForGrid } from '../../hooks/useUrlQueryParamsForGrid';
import { ApplicationState } from '../../store';
import { UserAgreementsState } from './store/store';
import { actions } from './store/actions';
import { connectComponent, withLoading } from '../../utils';
import { dateTimeFormat } from '../../constants/date-format';

type UserAgreementsListProps = UserAgreementsState & typeof actions;

const StudyCenterUserAgreements: FC<UserAgreementsListProps> = ({ agreements, agreementsCount, getAgreements }) => {
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [modalText, setModalText] = useState('');

    const {
        pagination,
        queryState: [inputQuery, setInputQuery],
    } = useUrlQueryParamsForGrid({
        total: agreementsCount,
        loadFn: getAgreements,
    });

    const showAgreement = (text: string) => {
        setModalText(text);
        setModalIsOpen(true);
    };

    const getGridData = (): Row[] =>
        agreements.map((item) => ({
            userName: {
                additionalComponent: (
                    <>
                        <div>{item.userName}</div>
                        <div>{item.userEmail}</div>
                    </>
                ),
            },
            creationDate: moment(item.creationDate).format(dateTimeFormat),
            personalAgreement: {
                align: 'center',
                additionalComponent: item.personalText && (
                    <Checkbox isChecked={item.personalAgreement} id={`personalAgreement-${item.userId}}`} isDisabled />
                ),
            },
            personalCheckBoxText: {
                additionalComponent: item.personalText && (
                    <Button
                        buttonClass="secondary"
                        pSize="small"
                        handleClick={() => showAgreement(item.personalCheckBoxText)}
                    >
                        Показать
                    </Button>
                ),
            },
            personalText: {
                additionalComponent: item.salesForceText && (
                    <Button
                        buttonClass="secondary"
                        pSize="small"
                        handleClick={() => showAgreement(item.salesForceText)}
                    >
                        Показать
                    </Button>
                ),
            },
            salesForce: {
                align: 'center',
                additionalComponent: item.salesForceText && (
                    <Checkbox isChecked={item.salesForce} id={`salesForce-${item.userId}}`} isDisabled />
                ),
            },
            salesForceCheckBoxText: {
                additionalComponent: item.salesForceText && (
                    <Button
                        buttonClass="secondary"
                        pSize="small"
                        handleClick={() => showAgreement(item.salesForceCheckBoxText)}
                    >
                        Показать
                    </Button>
                ),
            },
        }));

    return (
        <Container>
            <ModalWindow
                isOpen={modalIsOpen}
                size={'big'}
                onCloseClick={() => setModalIsOpen(false)}
                cancelButtonText="Закрыть"
            >
                <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(modalText) }} />
            </ModalWindow>

            <Column columnSize="50">
                <Title titleText="Соглашения клиентов УЦ" />
            </Column>
            <Column columnSize="100">
                <TextInput
                    id="query"
                    value={inputQuery}
                    onChangeHandler={(e) => setInputQuery(e.currentTarget.value)}
                    label={'Поиск (email, id, имя)'}
                />
            </Column>
            <Column columnSize="100">
                {agreements.length ? (
                    <Grid rows={getGridData()} columns={columns} pagination={pagination} />
                ) : (
                    <EmptyListWarning />
                )}
            </Column>
        </Container>
    );
};

export default connectComponent({
    mapStateToProps: (state: ApplicationState) => state.userAgreements,
    actions,
    component: withLoading(StudyCenterUserAgreements),
});

const columns = {
    userName: {
        value: 'адрес электронной почты + имя',
        columnSize: '30',
    },
    creationDate: {
        value: 'дата и время принятия соглашений',
        columnSize: '10',
    },
    personalAgreement: {
        value: 'факт согласия с обязательным соглашением',
        columnSize: '5',
    },
    personalCheckBoxText: {
        value: 'текст согласия с обязательным соглашением',
        columnSize: '10',
    },
    personalText: {
        value: 'текст Privacy Policy',
        columnSize: '10',
    },
    salesForce: {
        value: 'факт согласия с необязательным соглашением',
        columnSize: '5',
    },
    salesForceCheckBoxText: {
        value: 'текст согласия с необязательным соглашением',
        columnSize: '10',
    },
};
